import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Home, Maximize, Building, Sparkles, Download, FileText } from 'lucide-react';
import { Logo } from '../components/Logo';
import LeadMagnetModal from '../components/LeadMagnetModal';
import Breadcrumbs from '../components/Breadcrumbs';

const META_TITLE = "Premium 3 BHK Flats in Rahatani Pune | Curated 3 BHK Collection | Nova Homes";
const META_DESCRIPTION = "Compare premium 3 BHK flats in Rahatani Pune. Aurelia, Silver Woods and Sneha Avenue 3 BHK options — carpet area, price, amenities, and connectivity at a glance. From ₹95L to ₹1Cr+.";

const ThreeBhkCollectionPage = () => {
  const [leadMagnetOpen, setLeadMagnetOpen] = useState(false);

  useEffect(() => {
    document.title = META_TITLE;
    const desc = document.querySelector('meta[name="description"]');
    const orig = desc?.getAttribute('content');
    if (desc) desc.setAttribute('content', META_DESCRIPTION);

    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Premium 3 BHK Flats in Rahatani Pune — Nova Homes Collection",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "url": "https://novahomes.in/project/aurelia",      "name": "Aurelia — Premium 3 BHK in Shivraj Nagar Rahatani" },
        { "@type": "ListItem", "position": 2, "url": "https://novahomes.in/project/silver-woods", "name": "Silver Woods — 3 BHK in Kalewadi-Rahatani" },
        { "@type": "ListItem", "position": 3, "url": "https://novahomes.in/project/sneha-avenue", "name": "Sneha Avenue — 3 BHK and Duplex in Bhugaon" }
      ]
    };
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.text = JSON.stringify(schema);
    s.setAttribute('data-collection', '3bhk');
    document.head.appendChild(s);
    return () => {
      document.head.removeChild(s);
      if (desc && orig) desc.setAttribute('content', orig);
      document.title = 'Nova Homes Pune | 2 & 3 BHK Flats in Rahatani | Book Site Visit';
    };
  }, []);

  const projects = [
    {
      name: "Aurelia",
      slug: "aurelia",
      tagline: "Boutique 3 BHK · Only 28 units",
      price: "Starting ₹99.50L",
      carpet: "1009 sq ft",
      location: "Shivraj Nagar, Rahatani",
      image: "/images/aurelia.webp",
      imageAlt: "Aurelia premium 3 BHK boutique residences in Shivraj Nagar Rahatani",
      highlights: [
        "7-storey low-density development",
        "Only 28 exclusive units",
        "Earthquake-resistant RCC, Jaquar fittings",
        "Rooftop cafeteria deck & barbeque zone",
        "2 min from BRTS Road"
      ],
      bestFor: "Senior IT professionals seeking quiet luxury & low neighbour density."
    },
    {
      name: "Silver Woods",
      slug: "silver-woods",
      tagline: "Most spacious 3 BHK in this price band",
      price: "₹95L – ₹96L (3 BHK)",
      carpet: "960 sq ft",
      location: "Kalewadi-Rahatani",
      image: "/images/silverwoods.webp",
      imageAlt: "Silver Woods premium 3 BHK apartments in Kalewadi-Rahatani Pune near DMart and BRTS",
      highlights: [
        "960 sq ft carpet — among the most spacious in segment",
        "Rooftop open gym, yoga & meditation deck",
        "800m from DMart Thergaon",
        "5 min from Sant Tukaram Nagar Metro",
        "RERA P52100054401"
      ],
      bestFor: "Dual-income couples, hybrid-working families wanting amenity-rich daily life."
    },
    {
      name: "Sneha Avenue",
      slug: "sneha-avenue",
      tagline: "Spacious 3 BHK & Duplex · Nature-led",
      price: "Starting ₹95L (3 BHK)",
      carpet: "800–950 sq ft",
      location: "Bhugaon, Pune",
      image: "/images/sneha-avenue.webp",
      imageAlt: "Sneha Avenue 3 BHK and duplex residences in Bhugaon Pune with hill views",
      highlights: [
        "Duplex configurations available",
        "Surrounded by hills & greenery",
        "2.8 km from Chandani Chowk",
        "Solar water heating + rainwater harvesting",
        "Vanaz Metro (Line 2) ~10 min"
      ],
      bestFor: "Families wanting low-traffic suburban peace with quick city access."
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="3bhk-collection-page">
      {/* Header */}
      <header className="bg-[#0a0a0a] border-b border-white/[0.04] sticky top-0 z-40">
        <div className="container-custom h-[72px] flex items-center justify-between">
          <Logo variant="light" />
          <div className="flex items-center gap-4">
            <Link to="/blog" className="hidden md:inline-block text-white/60 hover:text-white text-xs uppercase tracking-[0.2em] font-medium transition-colors">Insights</Link>
            <Link to="/" className="flex items-center gap-1.5 text-white/40 hover:text-white/70 text-sm font-light transition-colors">
              <ArrowLeft size={15} /><span className="hidden sm:inline">Back to Home</span>
            </Link>
          </div>
        </div>
        <div className="container-custom py-4">
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Premium 3 BHK Collection' }]} />
        </div>
      </header>

      {/* Hero */}
      <section data-testid="3bhk-hero" className="relative bg-[#0a0a0a] text-white py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src="/images/aurelia.webp" alt="Premium 3 BHK residential development in Rahatani Pune" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-[#0a0a0a]/40" />
        </div>
        <div className="container-custom relative max-w-4xl">
          <p className="text-[#C5A46D] text-xs uppercase tracking-[0.3em] font-medium mb-6 flex items-center gap-2">
            <Sparkles size={14} /> Curated 3 BHK Collection · 2026
          </p>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] mb-8">
            Premium 3 BHK Flats in Rahatani &amp; Bhugaon
          </h1>
          <p className="text-white/70 text-lg md:text-xl font-light leading-relaxed mb-8 max-w-3xl">
            Three thoughtfully positioned 3 BHK addresses — boutique, mainstream, and nature-led — each engineered for a different style of living. Compare side-by-side and pick the one that mirrors your life.
          </p>
        </div>
      </section>

      {/* Comparison table */}
      <section data-testid="3bhk-compare-table" className="py-20 md:py-28 bg-white">
        <div className="container-custom max-w-5xl">
          <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-3">At a Glance</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111] leading-tight mb-10">
            Side-by-Side Comparison
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full text-left border border-[#111]/[0.08] text-sm">
              <thead className="bg-[#0a0a0a] text-white text-xs uppercase tracking-wider">
                <tr>
                  <th className="py-4 px-5 font-medium">Project</th>
                  <th className="py-4 px-5 font-medium">Carpet Area</th>
                  <th className="py-4 px-5 font-medium">Price</th>
                  <th className="py-4 px-5 font-medium hidden md:table-cell">Location</th>
                  <th className="py-4 px-5 font-medium hidden md:table-cell">Best For</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p, i) => (
                  <tr key={p.slug} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAF8]'}>
                    <td className="py-4 px-5 font-semibold text-[#111]">
                      <Link to={`/project/${p.slug}`} data-testid={`compare-row-${p.slug}`} className="hover:text-[#C5A46D] transition-colors">
                        {p.name}
                      </Link>
                      <p className="text-[#999] text-xs font-light mt-0.5">{p.tagline}</p>
                    </td>
                    <td className="py-4 px-5 text-[#444] font-light">{p.carpet}</td>
                    <td className="py-4 px-5 text-[#111] font-medium">{p.price}</td>
                    <td className="py-4 px-5 text-[#444] font-light hidden md:table-cell">{p.location}</td>
                    <td className="py-4 px-5 text-[#444] font-light hidden md:table-cell">{p.bestFor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Detail cards */}
      <section className="py-20 md:py-28 bg-[#FAFAF8]">
        <div className="container-custom max-w-5xl">
          <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-3">Detailed Profiles</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111] leading-tight mb-12">
            Inside Each 3 BHK
          </h2>

          <div className="space-y-8">
            {projects.map((p, idx) => (
              <div
                key={p.slug}
                data-testid={`3bhk-detail-${p.slug}`}
                className="bg-white border border-[#111]/[0.06] grid md:grid-cols-[40%_1fr] overflow-hidden"
              >
                <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
                  <img src={p.image} alt={p.imageAlt} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <p className="text-[#C5A46D] text-[10px] uppercase tracking-[0.2em] font-medium mb-2">{p.tagline}</p>
                  <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#111] mb-2">{p.name}</h3>
                  <p className="text-[#999] text-sm font-light mb-5 flex items-center gap-2"><Maximize size={13} /> {p.carpet} carpet · {p.location}</p>

                  <ul className="space-y-2 mb-6">
                    {p.highlights.map((h, j) => (
                      <li key={j} className="flex items-start gap-3 text-[#444] text-sm font-light leading-relaxed">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#C5A46D] flex-shrink-0 mt-2" />{h}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center justify-between pt-5 border-t border-[#111]/[0.06]">
                    <span className="text-[#111] font-medium">{p.price}</span>
                    <Link
                      to={`/project/${p.slug}`}
                      className="inline-flex items-center gap-2 text-[#C5A46D] text-sm font-medium hover:gap-3 transition-all"
                    >
                      View {p.name} <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead-magnet CTA band */}
      <section className="py-16 md:py-20 bg-[#0a0a0a] text-white">
        <div className="container-custom max-w-4xl">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="w-14 h-14 bg-[#C5A46D]/15 text-[#C5A46D] flex items-center justify-center flex-shrink-0 rounded-sm">
              <FileText size={22} />
            </div>
            <div className="flex-1">
              <p className="text-[#C5A46D] text-[10px] uppercase tracking-[0.3em] font-medium mb-2">Free PDF</p>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-2 leading-tight">
                Get the full Rahatani Connectivity Map
              </h2>
            </div>
            <button
              type="button"
              onClick={() => setLeadMagnetOpen(true)}
              data-testid="3bhk-download-map-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#C5A46D] text-[#0a0a0a] py-3.5 px-6 font-medium text-sm tracking-wide hover:bg-[#D4AF37] transition-colors flex-shrink-0"
            >
              <Download size={16} /> Download
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] text-white py-16 border-t border-white/[0.04]">
        <div className="container-custom text-center">
          <Logo variant="light" className="justify-center mb-6" />
          <p className="text-white/40 text-sm font-light mb-2">Nova Homes · Pune, Maharashtra</p>
          <p className="text-white/30 text-xs font-light">© 2026 Nova Homes. All rights reserved.</p>
        </div>
      </footer>

      <LeadMagnetModal open={leadMagnetOpen} onClose={() => setLeadMagnetOpen(false)} source="3bhk-collection-page" />
    </div>
  );
};

export default ThreeBhkCollectionPage;
